import React, { useState, useEffect, lazy, Suspense } from 'react'
import { Route, Routes, Navigate, useLocation } from 'react-router-dom'
// Removed bulk import, styles are pulled in via main.jsx
import Layout from './components/Layout'
import Login from './components/Auth/Login'
import Register from './components/Auth/Register'
import VerifyEmail from './components/Auth/VerifyEmail'
import ForgotPassword from './components/Auth/ForgotPassword'
import ResetPassword from './components/Auth/ResetPassword'
import HeroPage from './components/HeroPage'
const Settings = lazy(() => import('./components/Settings/Settings'))
import Legal from './components/Legal/Legal'
import LegalDoc from './components/Legal/LegalDoc'
import Help from './components/Help/Help'
import DisplayPartnerGuide from './components/Help/DisplayPartnerGuide'
import Contact from './components/Contact'
import EventEdit from './components/EventEdit'
import DisplayPartnerRecruitment from './components/DisplayPartnerRecruitment'
import ConciergePreview from './components/Concierge/ConciergePreview'
const Profile = lazy(() => import('./components/Profile'))

const EventSubmission = lazy(() => import('./EventSubmission'))
const BusinessRegistration = lazy(() => import('./BusinessRegistration'))
const BusinessDirectory = lazy(() => import('./components/BusinessDirectory'))
const Dashboard = lazy(() => import('./components/Dashboard/Dashboard'))
const AdminAction = lazy(() => import('./components/Admin/AdminAction.jsx'))
const RequestAdminAccess = lazy(() => import('./components/Admin/RequestAdminAccess'))
const UserDashboard = lazy(() => import('./components/Dashboard/User/UserDashboard'))
const EventBuilder = lazy(() => import('./components/EventBuilder/EventBuilder'))
const EventPreview = lazy(() => import('./components/EventPreview'))
const TechnicalOwnerDashboard = lazy(() => import('./components/Dashboard/Admin/TechnicalOwnerDashboard'))
const DisplayAdminDashboard = lazy(() => import('./components/Dashboard/Admin/DisplayAdminDashboard'))
const ModeratorDashboard = lazy(() => import('./components/Dashboard/Admin/ModeratorDashboard'))
const ProjectMap = lazy(() => import('./components/ProjectMap'))
const ConciergeDashboard = lazy(() => import('./components/Concierge/ConciergeDashboard'))

const App = () => {
    const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000'
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const location = useLocation()

    useEffect(() => {
        const checkAuth = () => {
            const token = localStorage.getItem('token')
            setIsAuthenticated(!!token)
        }

        checkAuth()

        window.addEventListener('storage', checkAuth)
        return () => window.removeEventListener('storage', checkAuth)
    }, [])

    useEffect(() => {
        const token = localStorage.getItem('token')
        setIsAuthenticated(!!token)
    }, [location.pathname])

    return (
        <Layout>
            <Suspense fallback={<div className="p-6 text-center text-muted">Loading...</div>}>
                <Routes>
                    <Route path="/" element={<HeroPage />} />
                    <Route
                        path="/login"
                        element={
                            !isAuthenticated ? (
                                <Login onLogin={() => setIsAuthenticated(true)} />
                            ) : (
                                <Navigate to="/user-dashboard" />
                            )
                        }
                    />
                    <Route
                        path="/register"
                        element={
                            !isAuthenticated ? (
                                <Register onRegister={() => setIsAuthenticated(true)} />
                            ) : (
                                <Navigate to="/user-dashboard" />
                            )
                        }
                    />
                    <Route
                        path="/verify-email"
                        element={<VerifyEmail onVerify={() => setIsAuthenticated(true)} />}
                    />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/event" element={<EventSubmission backendUrl={backendUrl} />} />
                    <Route path="/register-business" element={<BusinessRegistration backendUrl={backendUrl} />} />
                    <Route path="/directory" element={<BusinessDirectory backendUrl={backendUrl} />} />
                    <Route path="/display-partner-signup" element={<DisplayPartnerRecruitment />} />
                    <Route path="/admin-action" element={<AdminAction />} />
                    <Route
                        path="/profile"
                        element={
                            isAuthenticated ? <Profile /> : <Navigate to="/login" />
                        }
                    />
                    <Route
                        path="/settings"
                        element={
                            isAuthenticated ? <Settings /> : <Navigate to="/login" />
                        }
                    />
                    <Route
                        path="/help/display_partner_guide"
                        element={<DisplayPartnerGuide />}
                    />
                    <Route
                        path="/help"
                        element={<Help />}
                    />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/legal" element={<Legal />} />
                    <Route path="/legal/:doc" element={<LegalDoc />} />
                    <Route path="/dashboard/technical" element={<TechnicalOwnerDashboard />} />
                    <Route path="/dashboard/display" element={<DisplayAdminDashboard />} />
                    <Route path="/dashboard/moderator" element={<ModeratorDashboard />} />
                    {/* Concierge feature management */}
                    <Route path="/dashboard/concierge" element={<ConciergeDashboard />} />
                    {/* Preview route for Concierge Service behind feature flag (preview mode) */}
                    <Route path="/admin/concierge/preview" element={<ConciergePreview />} />
                    <Route path="/dashboard/project-map" element={<ProjectMap />} />
                    <Route
                        path="/user-dashboard"
                        element={isAuthenticated ? <UserDashboard /> : <Navigate to="/login" />}
                    />
                    <Route
                        path="/event-builder/:id"
                        element={isAuthenticated ? <EventBuilder /> : <Navigate to="/login" />}
                    />
                    <Route
                        path="/event-view/:id"
                        element={isAuthenticated ? <EventPreview /> : <Navigate to="/login" />}
                    />
                    <Route
                        path="/event/:id/edit"
                        element={isAuthenticated ? <EventEdit /> : <Navigate to="/login" />}
                    />
                </Routes>
            </Suspense>
        </Layout>
    )
}

export default App
